import React, { useEffect, useRef, useState } from "react";
import './styles.css';

interface ISpeechAnimation {
  vad: boolean;  // true when voice is detected
}

const SpeechAnimation: React.FC<ISpeechAnimation> = ({ vad }) => {
  const [bars, setBars] = useState<number[]>(Array(20).fill(1));
  const animationFrameRef = useRef<number>();
  const previousBarsRef = useRef<number[]>(Array(20).fill(1));
  const audioContextRef = useRef<AudioContext>();
  const analyserRef = useRef<AnalyserNode>();
  const dataArrayRef = useRef<Uint8Array>();

  useEffect(() => {
    // Initialize audio context and analyzer
    const initAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioContextRef.current = new AudioContext();
        analyserRef.current = audioContextRef.current.createAnalyser();
        
        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);
        
        analyserRef.current.fftSize = 64;
        const bufferLength = analyserRef.current.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(bufferLength);
      } catch (error) {
        console.error("Error accessing microphone:", error);
      }
    };

    initAudio();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (vad) {
      const animateBars = () => {
        if (!analyserRef.current || !dataArrayRef.current) return;

        // Get frequency data
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        
        // Calculate new heights based on frequency data
        const newBars = Array(20).fill(0).map((_, idx) => {
          const dataIndex = Math.floor(idx * dataArrayRef.current!.length / 20);
          const value = dataArrayRef.current![dataIndex];
          // Convert to percentage (0-100) and smooth with previous value
          const target = (value / 255) * 100;
          return previousBarsRef.current[idx] + (target - previousBarsRef.current[idx]) * 0.15;
        });
        
        previousBarsRef.current = newBars;
        setBars(newBars);
        animationFrameRef.current = requestAnimationFrame(animateBars);
      };

      animateBars();
    } else {
      // Smoothly return to idle state
      const fadeOut = () => {
        const newBars = previousBarsRef.current.map((prevHeight) => {
          // Move toward 1 (idle state) with smooth transition
          return prevHeight + (1 - prevHeight) * 0.1;
        });

        previousBarsRef.current = newBars;
        setBars(newBars);

        if (Math.max(...newBars) > 2) {
          animationFrameRef.current = requestAnimationFrame(fadeOut);
        }
      };

      fadeOut();
    }

    // Cleanup animation frame on unmount or vad change
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [vad]);

  return (
    <div id="bars">
      {bars.map((bar, idx) => (
        <div
          key={idx}
          className={"bar"}
          style={{
            height: `${bar}%`,
          }}
        ></div>
      ))}
    </div>
  );
};

export default SpeechAnimation;
